import React, { useMemo, useState } from 'react';
import PageHeader from '../components/ui/PageHeader';
import { useT } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import ReportFormatModal from '../components/reports/ReportFormatModal';
import { downloadJSON } from '../utils/format';
import { useData } from '../data/DataContext';
import { useApi } from '../api/client';
import { getReport } from '../api/reports';

export default function ReportsPage() {
  const t = useT();
  const toast = useToast();
  const data = useData();
  const [selected, setSelected] = useState(null);

  const cards = useMemo(
    () => [
      { id: 'members', icon: '👥', title: t('reports.cards.members_title'), text: t('reports.cards.members_text'), source: () => data.members },
      { id: 'events', icon: '🕊️', title: t('reports.cards.events_title'), text: t('reports.cards.events_text'), source: () => data.events },
      { id: 'finance', icon: '💰', title: t('reports.cards.finance_title'), text: t('reports.cards.finance_text'), source: () => data.payments },
      { id: 'monthly', icon: '📅', title: t('reports.cards.monthly_title'), text: t('reports.cards.monthly_text'), source: () => data.payments },
      { id: 'member-history', icon: '👤', title: t('reports.cards.member_history_title'), text: t('reports.cards.member_history_text'), source: () => data.payments },
      { id: 'official', icon: '🏛️', title: t('reports.cards.official_title'), text: t('reports.cards.official_text'), source: () => ({ members: data.members, events: data.events, payments: data.payments }) },
    ],
    [t, data]
  );

  const handleGenerate = async ({ period, format }) => {
    if (!selected) return;
    let body;
    if (useApi) {
      try {
        const summary = await getReport(selected.id, { period });
        body = { format, ...summary };
      } catch (e) {
        toast.error(e.message || t('toast.api_error'));
        return;
      }
    } else {
      body = {
        report: selected.id,
        period,
        format,
        generatedAt: new Date().toISOString(),
        data: selected.source(),
      };
    }
    downloadJSON(`${selected.id}-${period}.json`, body);
    toast.success(t('reports.toast_generated', { name: selected.title }));
    setSelected(null);
  };

  return (
    <div>
      <PageHeader
        icon="📜"
        title={t('reports.section_title')}
        subtitle={t('reports.section_subtitle')}
      />

      <div className="report-grid">
        {cards.map((card) => (
          <div className="report-card" key={card.id} onClick={() => setSelected(card)} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(card); } }}>
            <div className="report-card-icon" aria-hidden>{card.icon}</div>
            <h4>{card.title}</h4>
            <p>{card.text}</p>
            <div className="export-btns">
              <button
                type="button"
                className="export-btn export-pdf"
                onClick={(e) => { e.stopPropagation(); setSelected({ ...card, format: 'pdf' }); }}
              >
                {t('reports.format_pdf')}
              </button>
              <button
                type="button"
                className="export-btn export-excel"
                onClick={(e) => { e.stopPropagation(); setSelected({ ...card, format: 'excel' }); }}
              >
                {t('reports.format_excel')}
              </button>
            </div>
          </div>
        ))}
      </div>

      <ReportFormatModal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        onSubmit={handleGenerate}
        report={selected}
      />
    </div>
  );
}
